import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare, Star, Eye, EyeOff, Pencil, Trash2, Plus, X, Search,
} from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import toast from 'react-hot-toast';
import type { Review } from '@/types';

const AdminReviewsPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const reviews = useAdminStore((s) => s.reviews);
  const artworks = useAdminStore((s) => s.artworks);
  const addReview = useAdminStore((s) => s.addReview);
  const updateReview = useAdminStore((s) => s.updateReview);
  const deleteReview = useAdminStore((s) => s.deleteReview);
  const toggleReviewVisibility = useAdminStore((s) => s.toggleReviewVisibility);

  const [search, setSearch] = useState('');
  const [artworkFilter, setArtworkFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'visible' | 'hidden'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);

  // Form state
  const [formArtworkId, setFormArtworkId] = useState('');
  const [formAuthor, setFormAuthor] = useState('');
  const [formRating, setFormRating] = useState(5);
  const [formComment, setFormComment] = useState('');

  const filtered = useMemo(() => {
    let items = [...(reviews ?? [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (artworkFilter !== 'all') items = items.filter((r) => r.artworkId === artworkFilter);
    if (statusFilter === 'visible') items = items.filter((r) => !r.isHidden);
    if (statusFilter === 'hidden') items = items.filter((r) => r.isHidden);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((r) => r.authorName.toLowerCase().includes(q) || r.comment.toLowerCase().includes(q));
    }
    return items;
  }, [reviews, artworkFilter, statusFilter, search]);

  const getArtworkTitle = (artworkId: string): string => {
    const artwork = (artworks ?? []).find((a) => a._id === artworkId);
    return artwork ? localize(artwork.title) : artworkId;
  };

  const openAddModal = (): void => {
    setEditingReview(null);
    setFormArtworkId((artworks ?? [])[0]?._id || '');
    setFormAuthor('');
    setFormRating(5);
    setFormComment('');
    setShowModal(true);
  };

  const openEditModal = (review: Review): void => {
    setEditingReview(review);
    setFormArtworkId(review.artworkId);
    setFormAuthor(review.authorName);
    setFormRating(review.rating);
    setFormComment(review.comment);
    setShowModal(true);
  };

  const handleSave = (): void => {
    if (!formArtworkId || !formAuthor.trim() || !formComment.trim()) return;
    if (editingReview) {
      updateReview(editingReview._id, {
        artworkId: formArtworkId,
        authorName: formAuthor.trim(),
        rating: formRating,
        comment: formComment.trim(),
      });
      toast.success(t.reviews.updated);
    } else {
      addReview({
        artworkId: formArtworkId,
        authorName: formAuthor.trim(),
        rating: formRating,
        comment: formComment.trim(),
        isHidden: false,
      });
      toast.success(t.reviews.submitted);
    }
    setShowModal(false);
  };

  const handleDelete = (id: string): void => {
    if (!confirm(t.reviews.deleteConfirm)) return;
    deleteReview(id);
    toast.success(t.reviews.deleted);
  };

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">{t.reviews.allReviews}</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {(reviews ?? []).length} total
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-black/90 dark:hover:bg-white/90 transition-all"
        >
          <Plus size={14} />
          {t.reviews.addReview}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by author or comment..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-transparent text-[11px] placeholder:text-black/25 dark:placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10"
          />
        </div>
        <select
          value={artworkFilter}
          onChange={(e) => setArtworkFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-transparent text-[11px] font-medium focus:outline-none cursor-pointer"
        >
          <option value="all">All Artworks</option>
          {(artworks ?? []).map((a) => (
            <option key={a._id} value={a._id}>{localize(a.title)}</option>
          ))}
        </select>
        <div className="flex gap-1">
          {(['all', 'visible', 'hidden'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] rounded-lg transition-all ${
                statusFilter === s
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
              }`}
            >
              {s === 'all' ? 'All' : s === 'visible' ? t.reviews.visible : t.reviews.hidden}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filtered.length > 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-black/[0.04] dark:border-white/[0.04]">
                {[t.reviews.artwork, t.reviews.author, t.reviews.rating, t.reviews.comment, t.reviews.status, t.reviews.date, ''].map((h, i) => (
                  <th key={i} className="text-left px-4 py-3 text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((review) => (
                <tr key={review._id} className="border-b border-black/[0.02] dark:border-white/[0.02] hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-[11px] font-medium truncate block max-w-[180px]">{getArtworkTitle(review.artworkId)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[12px] font-medium">{review.authorName}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} size={12} className={star <= review.rating ? 'text-amber-400' : 'text-black/10 dark:text-white/10'} fill={star <= review.rating ? 'currentColor' : 'none'} />
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] text-black/50 dark:text-white/50 truncate block max-w-[250px]">{review.comment}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                      review.isHidden ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'
                    }`}>
                      {review.isHidden ? t.reviews.hidden : t.reviews.visible}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] text-black/40 dark:text-white/40">{formatDate(review.createdAt)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleReviewVisibility(review._id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all"
                        title={review.isHidden ? t.reviews.visible : t.reviews.hidden}
                      >
                        {review.isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button
                        onClick={() => openEditModal(review)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all"
                        title={t.reviews.editReview}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(review._id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-black/30 dark:text-white/30 hover:text-red-500 hover:bg-red-500/5 transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
            <MessageSquare size={24} className="text-black/10 dark:text-white/10" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            {(reviews ?? []).length === 0 ? t.reviews.noReviews : 'No reviews match this filter'}
          </p>
        </div>
      )}

      {/* Modal for Add/Edit */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#111] border border-black/[0.06] dark:border-white/[0.06] rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 space-y-5"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-syncopate font-bold tracking-tight">
                  {editingReview ? t.reviews.editReview : t.reviews.addReview}
                </h2>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:bg-black/[0.04] dark:hover:bg-white/[0.04] transition-all">
                  <X size={16} />
                </button>
              </div>

              {/* Artwork select */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">{t.reviews.artwork}</label>
                <select
                  value={formArtworkId}
                  onChange={(e) => setFormArtworkId(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10"
                >
                  {(artworks ?? []).map((a) => (
                    <option key={a._id} value={a._id}>{localize(a.title)}</option>
                  ))}
                </select>
              </div>

              {/* Author */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">{t.reviews.author}</label>
                <input
                  type="text"
                  value={formAuthor}
                  onChange={(e) => setFormAuthor(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10"
                />
              </div>

              {/* Rating */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">{t.reviews.rating}</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormRating(star)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star size={24} className={star <= formRating ? 'text-amber-400' : 'text-black/15 dark:text-white/15'} fill={star <= formRating ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40">{t.reviews.comment}</label>
                <textarea
                  value={formComment}
                  onChange={(e) => setFormComment(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-transparent text-sm focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!formArtworkId || !formAuthor.trim() || !formComment.trim()}
                  className="px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-widest rounded-xl hover:bg-black/90 dark:hover:bg-white/90 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  {editingReview ? t.reviews.updated : t.reviews.submit}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminReviewsPage;
